// react
import React from 'react';

// internationalization
import { useTranslation } from 'react-i18next';

// material ui icons
import SchoolRoundedIcon from '@mui/icons-material/SchoolRounded';
import WavingHandRoundedIcon from '@mui/icons-material/WavingHandRounded';

// material ui
import {
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';

// relative
import EnergyGroupTypeAvatar from '../energygroup/EnergyGroupTypeAvatar';

const EnergyXplorerBaseCard = ({ card, index }) => {
  const { t } = useTranslation(['energy-analysis', 'energyxplorer']);

  const theme = useTheme();

  return (
    <Card
      key={index}
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CardHeader
        title={card.title}
        subheader={card.subtitle}
        avatar={
          <Avatar
            sx={{
              bgcolor: card.bgColor,
              borderRadius: 3,
              width: 48,
              height: 48,
            }}
          >
            {React.cloneElement(card.icon, {
              sx: { color: card.color, fontSize: 32 },
            })}
          </Avatar>
        }
        action={card.chip && card.chip}
        slotProps={{
          title: { variant: 'h6', fontWeight: 'bold' },

          subheader: {
            variant: 'subtitle2',
            color: 'text.secondary',
            fontWeight: 600,
          }
        }} />
      <CardContent sx={{ p: 4, pt: 2, flexGrow: 1 }}>
        <Stack spacing={3}>
          {/* Description */}
          <Typography
            variant="subtitle2"
            color="text.secondary"
            sx={{ lineHeight: 1.7 }}
          >
            {card.description}
          </Typography>
          <Stack direction="row" spacing={2} alignItems="center">
            {card.supportedDataTypes?.map((type) => (
              <Box
                key={type}
                sx={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  mr: 1,
                }}
              >
                <EnergyGroupTypeAvatar type={type} />
                <Typography variant="caption" color="text.secondary">
                  {t('glossary:' + type.toLowerCase())}
                </Typography>
              </Box>
            ))}
          </Stack>

          {/* Features */}
          <Box>
            <Typography
              variant="subtitle2"
              fontWeight="bold"
              sx={{ mb: 2, color: theme.palette.primary.main }}
            >
              {t('sales_cards.key_features')}
            </Typography>
            <Stack spacing={1.5}>
              {card.features.map((feature, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Box
                    sx={{
                      width: 8,
                      height: 8,
                      borderRadius: '50%',
                      bgcolor: theme.palette.primary.main,
                      flexShrink: 0,
                    }}
                  />
                  <Typography variant="body2" color="textSecondary">
                    {feature}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </CardContent>
      <CardActions sx={{ p: 2, justifyContent: 'flex-end', display: 'flex' }}>
        {card.learnMoreLink && (
          <Button
            variant="contained"
            startIcon={<SchoolRoundedIcon />}
            href={card.learnMoreLink}
            target="_blank"
          >
            {t('sales_cards.read_more', { ns: 'energyxplorer' })}
          </Button>
        )}

        {card.contactLink && (
          <Button
            variant="contained"
            startIcon={<WavingHandRoundedIcon />}
            href={card.contactLink}
            target="_blank"
          >
            {t('sales_cards.get_started', { ns: 'energyxplorer' })}
          </Button>
        )}
      </CardActions>
    </Card>
  );
};

export default EnergyXplorerBaseCard;
